import { ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit } from '@angular/core'
import { combineLatest, Observable, Subject } from 'rxjs'
import { ComiteMembreService } from 'src/app/shared/data-access/http/comite-membre.service'
import { map } from 'rxjs/operators'
import { AsyncPipe, NgIf } from '@angular/common'
import { AgGridAngular } from 'ag-grid-angular'
import { CellClickedEvent, ColDef, ITooltipParams } from 'ag-grid-community'
import { localeFr } from '../../../../assets/localization/ag-grid/ag-grid-locale-fr'
import { ToastDomainService } from 'src/app/shared/ui/toast/domain/toast.domain'
import { TypeToast } from 'src/app/shared/ui/toast/state/toast.state'

@Component({
    selector: 'app-comite-list',
    templateUrl: './comite-list.component.html',
    styleUrls: ['./comite-list.component.scss'],
    imports: [NgIf, AsyncPipe, AgGridAngular]
})
export class ComiteListComponent implements OnInit, OnDestroy {
    @Input() withSuppression = false
    @Input() provenance!: string
    public codeComite = ''
    public resultsNumber = 0
    comiteMembreLoading$!: Observable<boolean>
    public resultsToShow$!: Observable<any>
    public exportIsSubmitting: boolean = false
    private unsubscribe$ = new Subject<void>()
    private cdr = inject(ChangeDetectorRef)
    private toastDomainService = inject(ToastDomainService)

    readonly localeFr = localeFr
    public colDefs: ColDef[] = [];

    defaultColDef: ColDef = {
        sortable: true,
        unSortIcon: true,
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true,
        filter: 'agTextColumnFilter',
        filterParams: { maxNumConditions: 1, filterOptions: ['contains', 'equals', 'notContains', 'notEqual'] },
    }

    constructor(
        private comiteMembreService: ComiteMembreService
    ) {
    }

    ngOnInit(): void {
        this.buildColumnDefs();
    }
    buildColumnDefs(): void {
        const baseColDefs: ColDef[] = [
            {
                headerName: 'Collaborateurs',
                valueGetter: (params) => params.data.nomCollaborateur + ' ' + params.data.prenomCollaborateur,
                flex: 2
            },
            {
                headerName: 'Direction / agence / service',
                valueGetter: (params) => params.data.entite.nomEntiteAffectation,
                flex: 2
            },
            {
                headerName: 'Emploi repère',
                valueGetter: (params) => params.data.emploi.nomEmploi,
                flex: 2
            }
        ]

        const finalColDefs = [...baseColDefs];

        if (this.withSuppression) {
            const actionCol: ColDef = {
                headerName: '',
                // ...
                cellRenderer: () => {
                    const eDiv = document.createElement('div');
                    eDiv.innerHTML = `<button class="btn remove-item ng-star-inserted"><i class="bi-trash" style="color: var(--color-ko);font-size: 1.5rem;cursor:pointer;text-align: center;display: flex;"></i></button>`;
                    return eDiv;
                },
                onCellClicked: (event: CellClickedEvent) => this.removeCollaborateur(event),
                tooltipValueGetter: (params: ITooltipParams) => {
                    const membreGroupe = params.data.nomCollaborateur + ' ' + params.data.prenomCollaborateur || '';
                    return `Supprime le membre ${membreGroupe} du comité`;
                },
                sortable: false,
                filter: false,
                width: 80,
                resizable: false,
            }
            finalColDefs.push(actionCol); // Ajoute la colonne d'actions
        }
        this.colDefs = finalColDefs;
    }

    getMembresComite(codeComite: string): void {
        this.codeComite = codeComite
        this.resultsToShow$ = this.comiteMembreService.getByCodeComite(codeComite)
        this.comiteMembreLoading$ = combineLatest([
            this.resultsToShow$.pipe(
                map((results: any[]) => (this.resultsNumber = results.length)),
                map(() => true), // Emit true for each value from this.resultsToShow$
                map(() => false) // Emit false when this.resultsToShow$ completes
            ),
        ]).pipe(map(([isRunning]) => isRunning))
    }

    public removeCollaborateur(rowData: CellClickedEvent) {
         this.comiteMembreService
            .deleteMembre(this.codeComite, rowData.data.matriculeCollaborateur)
            .subscribe({
                next: () => {
                    this.getMembresComite(this.codeComite);
                    this.toastDomainService.addToastMessage(
                        TypeToast.success,
                        `Suppression de membre du comité ${this.codeComite}`,
                        `Le collaborateur ${rowData.data.nomCollaborateur} ${rowData.data.prenomCollaborateur} a été supprimé du comité avec succès.`
                    );
                },
                error: () => {
                    this.toastDomainService.addToastMessage(
                        TypeToast.danger,
                        `Suppression de membre du comité ${this.codeComite}`,
                        'Erreur lors de la suppression du collaborateur'
                    );
                }
            })
    }

    ngOnDestroy() {
        this.unsubscribe$.next()
        this.unsubscribe$.complete()
    }

    exporterCSV(): void {
        this.exportIsSubmitting = true
        if (this.codeComite !== undefined) {
            this.comiteMembreService.exportCsvByCodeComite(this.codeComite).subscribe({
                next: (response) => {
                    if (response && response.headers) {
                        const body: Blob | null = response.body
                        if (body != null) {
                            //saveAs(body, filename)
                            this.toastDomainService.addToastMessage(
                                TypeToast.success,
                                "Export CSV",
                                "Le résultat de votre export du comité " + this.codeComite + " vous sera adressé d’ici quelques instants par mail au format Google Sheet."
                            );
                        } else {
                            this.toastDomainService.addToastMessage(
                                TypeToast.danger,
                                "Export CSV",
                                "Erreur lors de l'export du comité"
                            );
                        }
                        this.exportIsSubmitting = false
                        this.cdr.markForCheck()
                    }
                },
                error: () => {
                    this.toastDomainService.addToastMessage(
                        TypeToast.danger,
                        "Export CSV",
                        "Erreur lors de l'export du comité"
                    );
                    this.exportIsSubmitting = false
                },
            })
        }
    }
}
